import { BackEndValueType, ESdChangeReason, ESymbolCategory, ISymbol } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import { KDatatimePicker } from "../../../../data-entry/k-datetime-picker";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import "./parameter-default-value-input.scss";

export interface IParameterDefaultValueInputState {}

export interface IParameterDefaultValueInputProps {
  symbol?: ISymbol;
  valueType?: BackEndValueType;
  onValueChange?: (value: any) => void;
  /** Overrides the input placeholder (right panel keeps "Design Placeholder"). */
  placeholder?: string;
  /**
   * Flush variant for in-cell use (the parameters table): borderless input with no
   * top margin so it fills and centers in a grid cell. Default keeps the bordered,
   * label-spaced panel look.
   */
  borderless?: boolean;
}

class ParameterDefaultValueInput extends PureSdComponent<
  IParameterDefaultValueInputProps & IWithSDProps & IWithContextProp,
  IParameterDefaultValueInputState
> {
  constructor(props: IParameterDefaultValueInputProps & IWithSDProps & IWithContextProp) {
    super(props);
  }

  stringInput = (symbol: ISymbol) => {
    const borderless = !!this.props.borderless;
    return (
      <>
        <KuikaInput
          data-testid="workflowParameterDefaultValueInput"
          defaultValue={symbol.fixedValue}
          placeholder={this.props.placeholder ?? "Design Placeholder"}
          borderless={borderless}
          style={borderless ? { width: "100%" } : { marginTop: 12, width: "100%" }}
          onBlur={(e) => (symbol.fixedValue = e.target.value)}
          onFocus={(e) => {
            e.target.select();
          }}
        />
      </>
    );
  };

  numberInput = (symbol: ISymbol) => {
    const borderless = !!this.props.borderless;
    return (
      <>
        <KuikaInput
          data-testid="workflowParameterDefaultValueInput"
          defaultValue={symbol.fixedValue}
          type="number"
          placeholder={this.props.placeholder ?? "Design Placeholder"}
          borderless={borderless}
          style={borderless ? { width: "100%" } : { marginTop: 12, width: "100%" }}
          onBlur={(e) => {
            let value = e.target.value.replace(/^0+/, "");
            if (value === "") value = "0";
            symbol.fixedValue = value;
          }}
          onFocus={(e) => {
            e.target.select();
          }}
        />
      </>
    );
  };

  dateTimeInput = (symbol: ISymbol) => {
    const borderless: boolean = !!this.props.borderless;
    // `symbol.fixedValue` holds a serialized date string (e.g. "2026-07-31T00:00:00"). An
    // empty/unset value must be passed as `undefined`, NOT "": KDatatimePicker only strips a
    // falsy `defaultValue` from the DatePicker when it is truthy, so an empty string leaks
    // through and antd renders "Invalid Date"/NaN. `undefined` makes the picker show its
    // placeholder. A set value is handed off verbatim; the picker parses it with the same
    // format it emits on change, so it round-trips.
    const rawFixedValue: unknown = symbol.fixedValue;
    const fixedValue: string | undefined =
      typeof rawFixedValue === "string" && rawFixedValue.length > 0 ? rawFixedValue : undefined;
    return (
      <>
        <KDatatimePicker
          // Lands on the picker's own <input>, not on a wrapper: antd spreads its rest props into
          // rc-picker, which folds `data-*` / `aria-*` into the input's props
          // (`getDataOrAriaProps` -> `mergedInputProps`). So this id addresses the editable value
          // itself, the same as the string and number editors above.
          data-testid="workflowParameterDefaultValueDatePicker"
          allowClear
          defaultValue={fixedValue}
          // `onChange` returns the picker's formatted string (or `undefined` when cleared);
          // store it as-is so it reads back to the same value via `defaultValue` on re-render.
          onChange={(value: string) => (symbol.fixedValue = value)}
          // In-cell the picker is flush (border stripped in SCSS); the panel keeps the
          // bordered property-grid look. `kuika_property-grid-input` also carries the
          // right-panel border, so it is dropped in the flush variant.
          className={borderless ? undefined : "kuika_property-grid-input"}
          display={this.props.valueType === "DateTimeOffset" ? "Date & Time" : "Date"}
        />
      </>
    );
  };

  booleanInput = (symbol: ISymbol): JSX.Element => {
    const borderless: boolean = !!this.props.borderless;
    // In-cell (table) the switch aligns to the same 16px left inset as every other cell's
    // content; the bordered panel keeps its original right-aligned placement.
    const wrapperClassName: string = classNames("kuika-wf-default-value__boolean", {
      "kuika-wf-default-value__boolean--flush": borderless
    });
    // `fixedValue` is untyped storage, so what comes back from a saved workflow is not
    // guaranteed to be a boolean. Coerced the same way the antd switch read it (truthiness),
    // because the kit's switch seeds real component state from this rather than an attribute.
    const defaultChecked: boolean = !!symbol.fixedValue;
    return (
      <div className={wrapperClassName}>
        <KuikaSwitch
          dataTestId="workflowParameterDefaultValueSwitch"
          labelPosition="none"
          defaultChecked={defaultChecked}
          onChange={(checked: boolean) => {
            this.props.onValueChange && this.props.onValueChange(checked);
          }}
        />
      </div>
    );
  };

  renderInputs = (symbol: ISymbol) => {
    let formInput = <></>;

    switch (this.props.valueType?.toLowerCase()) {
      case "string":
      case "unbound":
        formInput = this.stringInput(symbol);
        break;
      case "guid":
        formInput = this.stringInput(symbol);
        break;
      case "int16":
        formInput = this.numberInput(symbol);
        break;
      case "int32":
        formInput = this.numberInput(symbol);
        break;
      case "number":
        formInput = this.numberInput(symbol);
        break;
      case "int64":
        formInput = this.numberInput(symbol);
        break;
      case "decimal":
        formInput = this.numberInput(symbol);
        break;
      case "double":
        formInput = this.numberInput(symbol);
        break;
      case "single":
        formInput = this.numberInput(symbol);
        break;
      case "boolean":
        formInput = this.booleanInput(symbol);
        break;
      case "date":
      case "datetime":
      case "datetimeoffset":
        formInput = this.dateTimeInput(symbol);
        break;
    }
    return formInput;
  };

  getCategoryName = (symbolPickerNodeCategory: ESymbolCategory): string => {
    switch (symbolPickerNodeCategory) {
      case ESymbolCategory.fixed:
        return "Fixed Value";
      default:
        return ESymbolCategory[symbolPickerNodeCategory];
    }
  };

  render() {
    const symbol = this.props.symbol;
    return (
      <div className={classNames("kuika_styling_typography_wrapper")} style={{ width: "100%" }}>
        {this.renderInputs(symbol!)}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangePropertyType ||
      changeReason === ESdChangeReason.ChangedCustomFieldOnPropertyPanel ||
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const parameterDefaultValueInput = withSD<IParameterDefaultValueInputProps>(
  withContext<IParameterDefaultValueInputProps>(ParameterDefaultValueInput)
);
export { parameterDefaultValueInput as ParameterDefaultValueInput };
